import { DynamicPricingCore } from './dynamic-pricing-core.js';
import { DynamicPricingCart } from './dynamic-pricing-cart.js';

class DynamicPricingBrowse extends DynamicPricingCore {
    constructor(config) {
        super(config);


        if (!this.config.enabled) {
            return;
        }

        this.priceElements = [];
        this.addButtonElements = [];
        this.productVariants = new Map();
        this.currentVariantPrices = new Map();
        this.priceObserver = null;
        this.productCardObservers = new Map();
        this.isUpdatingPrice = false;
        this.compareDrawerUpdateTimeout = null;
        this.processedElements = new WeakSet();
        this.processingElements = new WeakSet(); // Track elements currently being processed
        this.domObserver = null;
        this.dynamicContentTimeout = null;
        this.pendingFetches = new Map(); // Track in-flight SKU fetches

        this.init();
    }

    init() {
        // Hide all price elements immediately
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');
        allPriceElements.forEach(element => {
            element.style.opacity = '0';
            element.style.visibility = 'hidden';
        });

        this.collectAllSkus();

        if (this.priceElements.length === 0 && this.productVariants.size === 0) {
            this.setupDynamicContentObserver();
            return;
        }

        this.showLoadingState();
        this.loadDynamicPrices();
        this.setupEventListeners();
        this.setupCompareDrawerWatcher();
        this.setupDynamicContentObserver();
    }

    /**
     * Update ALL variant buttons on the page with dynamic prices
     */
    updateAllVariantButtonPrices(pricesMap) {
        const allVariantButtons = document.querySelectorAll('button[data-sku][data-price], .custom-variant-option-product-card[data-sku][data-price]');

        allVariantButtons.forEach(button => {
            const sku = button.dataset.sku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                const priceInCents = Math.round(price * 100);
                button.dataset.price = priceInCents;
            }
        });

        // Also update radio inputs
        const allRadioInputs = document.querySelectorAll('input[type="radio"][data-sku][data-price]');
        allRadioInputs.forEach(radio => {
            const sku = radio.dataset.sku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                radio.dataset.price = Math.round(price * 100);
            }
        });
    }

    /**
     * Set up MutationObserver to detect dynamically loaded product sections
     */
    setupDynamicContentObserver() {
        this.domObserver = new MutationObserver((mutations) => {
            let hasNewPriceElements = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const priceElements = node.querySelectorAll ?
                                node.querySelectorAll('[data-dynamic-price]') : [];

                            if (node.matches && node.matches('[data-dynamic-price]')) {
                                hasNewPriceElements = true;
                            }

                            if (priceElements.length > 0) {
                                hasNewPriceElements = true;
                            }
                        }
                    }
                }
            }

            if (hasNewPriceElements) {
                this.handleDynamicContent();
            }
        });

        this.domObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        document.addEventListener('shopify:section:load', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('recently-viewed:loaded', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('recommendations:loaded', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('product-recommendations:loaded', () => {
            this.handleDynamicContent();
        });
    }

    /**
     * Handle dynamically loaded content (recently viewed, recommendations, etc.)
     * Debounced to prevent multiple simultaneous calls
     */
    handleDynamicContent() {
        clearTimeout(this.dynamicContentTimeout);
        this.dynamicContentTimeout = setTimeout(() => {
            this._handleDynamicContentInternal();
        }, 100);
    }

    /**
     * Internal implementation of handleDynamicContent
     */
    async _handleDynamicContentInternal() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');
        const newElements = [];
        const newSkus = [];

        allPriceElements.forEach(element => {
            // Skip if already processed AND ready, or currently being processed
            if (this.processedElements.has(element) && element.classList.contains('dp-ready')) {
                return;
            }
            if (this.processingElements.has(element)) {
                return; // Already being handled by another call
            }

            this.processingElements.add(element); // Mark as in-progress immediately
            newElements.push(element);

            const sku = element.dataset.productSku;
            // Check cache AND pending fetches to avoid duplicate requests
            if (sku && !newSkus.includes(sku) && !this.priceCache[sku] && !this.pendingFetches.has(sku)) {
                newSkus.push(sku);
            }

            // Show loading state
            element.classList.add('dp-loading');
            element.classList.remove('dp-ready', 'dp-active');
            element.style.opacity = '1';
            element.style.visibility = 'visible';

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '0';
            if (priceSale) priceSale.style.opacity = '0';

            if (!element.querySelector('.loading-spinner')) {
                element.insertAdjacentHTML('beforeend', this.createSpinner());
            }
        });

        if (newElements.length === 0) {
            return;
        }

        const newProductJsons = document.querySelectorAll('[data-product-json]');
        newProductJsons.forEach(element => {
            try {
                const product = JSON.parse(element.textContent);
                const variants = product.variants || [];
                variants.forEach(v => {
                    if (v.sku && !newSkus.includes(v.sku) && !this.priceCache[v.sku] && !this.pendingFetches.has(v.sku)) {
                        newSkus.push(v.sku);
                    }
                });
            } catch (e) {}
        });

        let allPrices = { ...this.priceCache };

        if (newSkus.length > 0) {
            try {
                const cachedPrices = this.getPricesFromCache(newSkus);
                const uncachedSkus = newSkus.filter(sku => !cachedPrices[sku] && !this.pendingFetches.has(sku));

                if (uncachedSkus.length > 0) {
                    // Mark SKUs as pending BEFORE the fetch
                    const fetchPromise = this.fetchPrices(uncachedSkus);
                    uncachedSkus.forEach(sku => this.pendingFetches.set(sku, fetchPromise));

                    try {
                        const newPrices = await fetchPromise;
                        this.updateCache(newPrices);
                        allPrices = { ...this.priceCache, ...newPrices };
                    } finally {
                        // Clear pending status
                        uncachedSkus.forEach(sku => this.pendingFetches.delete(sku));
                    }
                }

                // Wait for any pending fetches for SKUs we need
                const pendingSkus = newSkus.filter(sku => this.pendingFetches.has(sku));
                if (pendingSkus.length > 0) {
                    await Promise.all([...new Set(pendingSkus.map(sku => this.pendingFetches.get(sku)))]);
                    allPrices = { ...this.priceCache };
                }
            } catch (error) {
                console.error('DP: handleDynamicContent fetch error:', error);
            }
        }

        newElements.forEach(element => {
            const sku = element.dataset.productSku;
            const price = allPrices[sku];

            if (price !== undefined) {
                this.updatePriceElement(element, price);
            } else {
                // No dynamic price - show original
                element.classList.remove('dp-loading');
                element.classList.add('dp-ready');

                const spinner = element.querySelector('.loading-spinner');
                if (spinner) spinner.remove();

                const priceRegular = element.querySelector('.f-price__regular');
                const priceSale = element.querySelector('.f-price__sale');
                if (priceRegular) priceRegular.style.opacity = '1';
                if (priceSale) priceSale.style.opacity = '1';

                element.style.opacity = '1';
                element.style.visibility = 'visible';
            }

            this.processedElements.add(element);
            this.processingElements.delete(element); // Clear in-progress flag
        });

        this.updateAllForms(allPrices);
        this.setupEventListeners(); // Re-setup listeners for new elements
    }

    showLoadingState() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');

        allPriceElements.forEach(element => {
            element.classList.add('dp-loading');
            element.classList.remove('dp-ready', 'dp-active');

            element.style.opacity = '1';
            element.style.visibility = 'visible';
            element.style.pointerEvents = 'auto';

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '0';
            if (priceSale) priceSale.style.opacity = '0';

            let spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            element.insertAdjacentHTML('beforeend', this.createSpinner());
        });
    }

    hideLoadingState() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');

        allPriceElements.forEach(element => {
            element.classList.remove('dp-loading');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '1';
            if (priceSale && !element.classList.contains('dp-active')) {
                priceSale.style.opacity = '1';
            }

            this.processedElements.add(element);
        });
    }

    collectAllSkus() {
        this.priceElements = document.querySelectorAll('[data-dynamic-price]');
        this.addButtonElements = document.querySelectorAll('button[data-sku][data-price]');

        const productJsonElements = document.querySelectorAll('[data-product-json]');

        productJsonElements.forEach(element => {
            try {
                const product = JSON.parse(element.textContent);
                const productId = product.id;
                const variants = product.variants || [];

                const variantSkus = variants
                    .map(v => v.sku)
                    .filter(sku => sku && sku.trim() !== '');

                if (variantSkus.length > 0) {
                    this.productVariants.set(productId, {
                        variants: variants,
                        skus: variantSkus,
                        element: element
                    });
                }
            } catch (e) {}
        });
    }

    collectSkus() {
        const skus = [];

        // From add buttons
        this.addButtonElements.forEach(element => {
            const sku = element.dataset.sku;
            if (sku && !skus.includes(sku)) {
                skus.push(sku);
            }
        });

        // From price elements
        this.priceElements.forEach(element => {
            const sku = element.dataset.productSku;
            if (sku && !skus.includes(sku)) {
                skus.push(sku);
            }
        });

        // From product variants JSON
        this.productVariants.forEach(productData => {
            productData.skus.forEach(sku => {
                if (!skus.includes(sku)) {
                    skus.push(sku);
                }
            });
        });

        const variantButtons = document.querySelectorAll('button[data-sku], .custom-variant-option-product-card[data-sku], input[type="radio"][data-sku]');
        variantButtons.forEach(element => {
            const sku = element.dataset.sku;
            if (sku && !skus.includes(sku)) {
                skus.push(sku);
            }
        });

        return skus;
    }

    setupEventListeners() {
        const possibleSelectors = [
            '.custom-variant-option-product-card',
            '.product-card [data-option-selector]',
            '.product-item [data-option-selector]',
            '.product-card select[name^="option"]',
            '.product-item select[name^="option"]',
            '.product-card input[type="radio"][name^="option"]',
            '.product-item input[type="radio"][name^="option"]',
            '.product-card input[type="radio"][data-id]',
            '.product-item input[type="radio"][data-id]',
            '.card input[type="radio"][data-id]',
            '.variant-picker',
            '.swatch-element',
            '.color-swatch'
        ];

        let variantPickers = [];
        for (const selector of possibleSelectors) {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                variantPickers = [...variantPickers, ...Array.from(elements)];
            }
        }

        // Remove duplicates
        variantPickers = [...new Set(variantPickers)];

        const self = this;

        variantPickers.forEach(picker => {
            // Skip if already has listener
            if (picker.dataset.dpListener) return;
            picker.dataset.dpListener = 'true';

            const eventType = (picker.tagName === 'SELECT' || picker.type === 'radio') ? 'change' : 'click';

            picker.addEventListener(eventType, function(e) {
                const productCard = this.closest('.product-card, .product-item, .product, [data-product-card], .card');
                if (!productCard) return;

                const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
                priceElements.forEach(el => {
                    el.classList.add('dp-updating');
                    el.classList.remove('dp-ready', 'dp-active');
                });
            }, true);

            picker.addEventListener(eventType, function(e) {
                const productCard = this.closest('.product-card, .product-item, .product, [data-product-card], .card');
                if (!productCard) return;

                const form = productCard.querySelector('form[action*="/cart/add"]');
                if (form) {
                    self.handleProductCardFormChange(form, productCard);

                    setTimeout(() => {
                        self.handleProductCardFormChange(form, productCard);
                    }, 50);
                }
            }, false);
        });
    }

    handleProductCardFormChange(form, productCard) {
        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput || !variantInput.value) {
            this.showProductCardPrices(productCard);
            return;
        }

        const variantId = parseInt(variantInput.value);
        const sku = this.getSkuFromVariantId(variantId, form);

        const preloadedPrice = this.currentVariantPrices.get(variantId);

        if (preloadedPrice !== undefined) {
            this.updateProductCardPrice(productCard, variantId, sku, preloadedPrice);
            return;
        }

        if (sku && this.priceCache[sku]) {
            this.currentVariantPrices.set(variantId, this.priceCache[sku]);
            this.updateProductCardPrice(productCard, variantId, sku, this.priceCache[sku]);
        } else if (sku) {
            this.fetchAndUpdateProductCardVariant(sku, variantId, productCard);
        } else {
            this.showProductCardPrices(productCard);
        }
    }

    updateVariantButtonPrices(container, sku, price) {
        const priceInCents = Math.round(price * 100);

        // Update buttons with data-price attribute
        const buttons = container.querySelectorAll(`button[data-sku="${sku}"][data-price]`);
        buttons.forEach(button => {
            button.dataset.price = priceInCents;
        });

        // Update radio inputs
        const radios = container.querySelectorAll(`input[type="radio"][data-sku="${sku}"]`);
        radios.forEach(radio => {
            radio.dataset.price = priceInCents;
        });

        // Update other elements
        const otherElements = container.querySelectorAll(`select[data-sku="${sku}"], [data-variant-sku="${sku}"]`);
        otherElements.forEach(element => {
            element.dataset.price = priceInCents;
        });
    }

    updateVariantSelectorsByVariantId(container, variantId, price) {
        const priceInCents = Math.round(price * 100);

        const selectors = container.querySelectorAll(`
            input[type="radio"][data-id="${variantId}"],
            button[data-variant-id="${variantId}"],
            option[value="${variantId}"]
        `);

        selectors.forEach(selector => {
            selector.dataset.price = priceInCents;

            if (selector.dataset.currency) {
                selector.dataset.formattedPrice = this.formatMoney(priceInCents);
            }
        });
    }

    updateProductCardPrice(productCard, variantId, sku, price) {
        this.isUpdatingPrice = true;

        const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
        const formattedPrice = this.formatMoney(price * 100);

        priceElements.forEach(element => {
            element.setAttribute('data-variant-id', variantId);

            const priceRegular = element.querySelector('.f-price__regular');
            const priceItem = priceRegular?.querySelector('.f-price-item--regular');

            if (priceItem) {
                priceItem.setAttribute('data-dynamic-pricing-active', 'true');
                priceItem.textContent = formattedPrice;
                priceRegular.style.opacity = '1';
                priceRegular.style.display = '';

                const priceSale = element.querySelector('.f-price__sale');
                if (priceSale) {
                    priceSale.style.display = 'none';
                    priceSale.style.opacity = '0';
                }

                element.classList.remove('f-price--on-sale', 'dp-loading', 'dp-updating');
                element.classList.add('dp-active', 'dp-ready');

                const spinner = element.querySelector('.loading-spinner');
                if (spinner) spinner.remove();

                element.style.opacity = '1';
                element.style.visibility = 'visible';
                element.style.pointerEvents = 'auto';
            }

            this.processedElements.add(element);
        });

        // Update variant button prices
        if (sku) {
            this.updateVariantButtonPrices(productCard, sku, price);
            this.updateVariantSelectorsByVariantId(productCard, variantId, price);
        }

        // const form = productCard.querySelector('form[action*="/cart/add"]');
        // if (form) {
        //     this.setHiddenPriceInput(form, price);
        // }

        setTimeout(() => { this.isUpdatingPrice = false; }, 50);
    }

    showProductCardPrices(productCard) {
        const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
        priceElements.forEach(el => {
            el.classList.remove('dp-updating', 'dp-loading');
            el.classList.add('dp-ready');

            const spinner = el.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            el.style.opacity = '1';
            el.style.visibility = 'visible';
            el.style.pointerEvents = 'auto';

            const priceRegular = el.querySelector('.f-price__regular');
            const priceSale = el.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '1';
            if (priceSale) priceSale.style.opacity = '1';

            this.processedElements.add(el);
        });
    }

    async fetchAndUpdateProductCardVariant(sku, variantId, productCard) {
        // Check if already fetching this SKU
        if (this.pendingFetches.has(sku)) {
            try {
                await this.pendingFetches.get(sku);
                if (this.priceCache[sku]) {
                    this.currentVariantPrices.set(variantId, this.priceCache[sku]);
                    this.updateProductCardPrice(productCard, variantId, sku, this.priceCache[sku]);
                } else {
                    this.showProductCardPrices(productCard);
                }
            } catch (error) {
                this.showProductCardPrices(productCard);
            }
            return;
        }

        try {
            const fetchPromise = this.fetchPrices([sku]);
            this.pendingFetches.set(sku, fetchPromise);

            const prices = await fetchPromise;
            this.updateCache(prices);
            this.pendingFetches.delete(sku);

            if (prices[sku]) {
                this.currentVariantPrices.set(variantId, prices[sku]);
                this.updateProductCardPrice(productCard, variantId, sku, prices[sku]);
            } else {
                this.showProductCardPrices(productCard);
            }
        } catch (error) {
            console.error('DP: fetchAndUpdateProductCardVariant error:', error);
            this.pendingFetches.delete(sku);
            this.showProductCardPrices(productCard);
        }
    }

    getSkuFromVariantId(variantId, form) {
        const productJson = form?.closest('.product, .product-card, .product-item, .card')?.querySelector('[data-product-json]');
        if (productJson) {
            try {
                const product = JSON.parse(productJson.textContent);
                const variant = product.variants.find(v => v.id == variantId);
                if (variant?.sku) {
                    return variant.sku;
                }
            } catch (e) {}
        }

        const allJsons = document.querySelectorAll('[data-product-json]');
        for (const jsonElement of allJsons) {
            try {
                const product = JSON.parse(jsonElement.textContent);
                const variant = product.variants.find(v => v.id == variantId);
                if (variant?.sku) {
                    return variant.sku;
                }
            } catch (e) {
                continue;
            }
        }

        return null;
    }

    async loadDynamicPrices() {
        try {
            const skus = this.collectSkus();

            // Also collect SKUs from all variant buttons
            const variantButtons = document.querySelectorAll('button[data-sku][data-price], .custom-variant-option-product-card[data-sku]');
            variantButtons.forEach(button => {
                const sku = button.dataset.sku;
                if (sku && !skus.includes(sku)) {
                    skus.push(sku);
                }
            });

            if (skus.length === 0) {
                this.hideLoadingState();
                this.showAllPricesAsFallback();
                return;
            }

            const cachedPrices = this.getPricesFromCache(skus);
            const uncachedSkus = skus.filter(sku => !cachedPrices[sku]);

            let allPrices = { ...cachedPrices };

            if (uncachedSkus.length > 0) {
                const newPrices = await this.fetchPrices(uncachedSkus);
                this.updateCache(newPrices);
                allPrices = { ...allPrices, ...newPrices };
            }

            // Update all prices
            this.updatePrices(allPrices);
            this.updateAllForms(allPrices);
            this.updateAllVariantButtonPrices(allPrices);
            this.preloadAllVariantPrices();
            this.hideLoadingState();

        } catch (error) {
            console.error('DP: loadDynamicPrices error:', error);
            this.hideLoadingState();
            this.showAllPricesAsFallback();
        }
    }

    showAllPricesAsFallback() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');
        allPriceElements.forEach(element => {
            element.classList.add('dp-ready');
            element.classList.remove('dp-loading');
            element.style.opacity = '1';
            element.style.visibility = 'visible';
            element.style.pointerEvents = 'auto';

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.display = '';
            if (priceSale) priceSale.style.display = '';

            this.processedElements.add(element);
        });
        const pricingErrorMessage = 'Please refresh page for price';
        document.querySelectorAll('.f-price-item').forEach(el => {
            el.innerHTML = `<span class="dp-error-message">${pricingErrorMessage}</span>`;
        });
    }

    preloadAllVariantPrices() {
        this.productVariants.forEach((productData) => {
            productData.variants.forEach(variant => {
                if (variant.sku && this.priceCache[variant.sku]) {
                    const price = this.priceCache[variant.sku];
                    this.currentVariantPrices.set(variant.id, price);

                    const productElement = productData.element?.closest('.product-card, .product-item, .product, [data-product-card], .card');
                    if (productElement) {
                        this.updateVariantButtonPrices(productElement, variant.sku, price);
                        this.updateVariantSelectorsByVariantId(productElement, variant.id, price);
                    }
                }
            });
        });
    }

    updateAllForms(pricesMap) {
        const productForms = document.querySelectorAll('form[action*="/cart/add"]');

        productForms.forEach(form => {
            const variantInput = form.querySelector('[name="id"]');
            if (!variantInput || !variantInput.value) {
                const sku = this.getDefaultSkuForForm(form);
                // if (sku && pricesMap[sku]) {
                //     this.setHiddenPriceInput(form, pricesMap[sku]);
                // }
                return;
            }

            const variantId = variantInput.value;
            const sku = this.getSkuFromVariantId(variantId, form);

            if (sku && pricesMap[sku]) {
                this.currentVariantPrices.set(parseInt(variantId), pricesMap[sku]);
                // this.setHiddenPriceInput(form, pricesMap[sku]);
            }
        });
    }

    getDefaultSkuForForm(form) {
        const priceElement = form.closest('.product, .product-card, .product-item, .card')?.querySelector('[data-product-sku]');
        if (priceElement) {
            return priceElement.dataset.productSku;
        }

        const productJson = form.closest('.product, .product-card, .product-item, .card')?.querySelector('[data-product-json]');
        if (productJson) {
            try {
                const product = JSON.parse(productJson.textContent);
                return product.selected_or_first_available_variant?.sku;
            } catch (e) {
                return null;
            }
        }

        return null;
    }

    // setHiddenPriceInput(form, price) {
    //     let existingInput = form.querySelector('[name="properties[__Dynamic Pricing__]"]');

    //     if (existingInput) {
    //         existingInput.value = price.toFixed(2);
    //     } else {
    //         const input = document.createElement('input');
    //         input.type = 'hidden';
    //         input.name = 'properties[__Dynamic Pricing__]';
    //         input.value = price.toFixed(2);
    //         input.setAttribute('data-dynamic-price-input', 'true');
    //         form.appendChild(input);
    //     }
    // }

    updatePrices(pricesMap) {
        this.addButtonElements.forEach(element => {
            const sku = element.dataset.sku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                element.dataset.price = Math.round(price * 100);
            }
        });

        this.priceElements.forEach(element => {
            const sku = element.dataset.productSku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                this.updatePriceElement(element, price);
            } else {
                // No dynamic price - still mark as ready
                element.classList.remove('dp-loading');
                element.classList.add('dp-ready');

                const spinner = element.querySelector('.loading-spinner');
                if (spinner) spinner.remove();

                element.style.opacity = '1';
                element.style.visibility = 'visible';

                this.processedElements.add(element);
            }
        });
    }

    /**
     * Update a single price element with dynamic price
     */
    updatePriceElement(element, price) {
        const formattedPrice = this.formatMoney(price * 100);
        const sku = element.dataset.productSku;

        const priceRegular = element.querySelector('.f-price__regular');
        const priceItem = priceRegular?.querySelector('.f-price-item--regular');

        if (priceItem) {
            priceItem.setAttribute('data-dynamic-pricing-active', 'true');
            priceItem.textContent = formattedPrice;
            priceRegular.style.display = '';
            priceRegular.style.opacity = '1';

            const priceSale = element.querySelector('.f-price__sale');
            if (priceSale) {
                priceSale.style.display = 'none';
            }

            element.classList.remove('f-price--on-sale', 'dp-loading', 'dp-updating');
            element.classList.add('dp-active', 'dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            element.style.opacity = '1';
            element.style.visibility = 'visible';
        }

        // Update variant button prices in the same product card
        const productCard = element.closest('.product-card, .product-item, .product, [data-product-card], .card');
        if (productCard && sku) {
            this.updateVariantButtonPrices(productCard, sku, price);
        }

        this.processedElements.add(element);
    }

    setupCompareDrawerWatcher() {
        const checkForDrawer = () => {
            const drawer = document.querySelector('#CompareDrawer, compare-drawer, [id*="CompareDrawer"]');

            if (drawer) {
                this.setupCompareDrawerObserver(drawer);
            } else {
                setTimeout(checkForDrawer, 100);
            }
        };

        checkForDrawer();

        const checkForQuickCompare = () => {
            const quickCompare = document.querySelector('quick-comparison-table, .quick-comparison-table-container');

            if (quickCompare) {
                this.setupCompareDrawerObserver(quickCompare);
            } else {
                setTimeout(checkForQuickCompare, 100);
            }
        };

        checkForQuickCompare();

        const compareEvents = [
            'compare:updated',
            'compare:drawer:opened',
            'compare:product:added',
            'compare:product:removed'
        ];

        compareEvents.forEach(eventName => {
            document.addEventListener(eventName, () => {
                setTimeout(() => this.updateCompareDrawerPrices(), 100);
            });
        });

        document.addEventListener('click', (e) => {
            const compareBtn = e.target.closest('[data-compare-add], [is="compare-add"], .compare-add');
            if (compareBtn) {
                setTimeout(() => this.updateCompareDrawerPrices(), 200);
            }
        });
    }

    setupCompareDrawerObserver(drawer) {
        const observer = new MutationObserver((mutations) => {
            const hasStructuralChange = mutations.some(mutation =>
                mutation.type === 'childList' && mutation.addedNodes.length > 0
            );

            const hasVisibilityChange = mutations.some(mutation =>
                mutation.type === 'attributes' &&
                (mutation.attributeName === 'hidden' || mutation.attributeName === 'class')
            );

            if (hasStructuralChange || hasVisibilityChange) {
                clearTimeout(this.compareDrawerUpdateTimeout);
                this.compareDrawerUpdateTimeout = setTimeout(() => {
                    this.updateCompareDrawerPrices();
                }, 100);
            }
        });

        observer.observe(drawer, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['hidden', 'class', 'open']
        });

        this.updateCompareDrawerPrices();
    }

    async updateCompareDrawerPrices() {
        const drawer = document.querySelector('#CompareDrawer, compare-drawer, [id*="CompareDrawer"]');
        const quickCompare = document.querySelector('quick-comparison-table, .quick-comparison-table-container');

        const container = drawer || quickCompare;
        if (!container) return;

        if (drawer && !quickCompare) {
            const isHidden = drawer.hasAttribute('hidden') ||
                drawer.classList.contains('hidden') ||
                getComputedStyle(drawer).display === 'none';

            if (isHidden) return;
        }

        const priceElements = container.querySelectorAll('[data-dynamic-price]');

        if (priceElements.length === 0) return;

        // Show loading state
        priceElements.forEach(element => {
            element.classList.add('dp-loading');
            element.classList.remove('dp-ready', 'dp-active');

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');

            if (priceRegular) priceRegular.style.opacity = '0';
            if (priceSale) priceSale.style.opacity = '0';

            if (!element.querySelector('.loading-spinner')) {
                element.insertAdjacentHTML('beforeend', this.createSpinner());
            }
        });

        const skus = [];
        const skuToElements = new Map();

        priceElements.forEach(element => {
            const sku = element.dataset.productSku;
            if (sku && !skus.includes(sku)) {
                skus.push(sku);
            }
            if (sku) {
                if (!skuToElements.has(sku)) {
                    skuToElements.set(sku, []);
                }
                skuToElements.get(sku).push(element);
            }
        });

        if (skus.length === 0) {
            this.hideCompareLoadingState(priceElements);
            return;
        }

        try {
            const cachedPrices = this.getPricesFromCache(skus);
            const uncachedSkus = skus.filter(sku => !cachedPrices[sku] && !this.pendingFetches.has(sku));

            let allPrices = cachedPrices;

            if (uncachedSkus.length > 0) {
                const fetchPromise = this.fetchPrices(uncachedSkus);
                uncachedSkus.forEach(sku => this.pendingFetches.set(sku, fetchPromise));

                try {
                    const newPrices = await fetchPromise;
                    this.updateCache(newPrices);
                    allPrices = { ...cachedPrices, ...newPrices };
                } finally {
                    uncachedSkus.forEach(sku => this.pendingFetches.delete(sku));
                }
            }

            // Wait for pending fetches
            const pendingSkus = skus.filter(sku => this.pendingFetches.has(sku));
            if (pendingSkus.length > 0) {
                await Promise.all([...new Set(pendingSkus.map(sku => this.pendingFetches.get(sku)))]);
                allPrices = { ...this.priceCache };
            }

            skuToElements.forEach((elements, sku) => {
                const price = allPrices[sku];
                if (price !== undefined) {
                    elements.forEach(element => {
                        this.updateComparePriceElement(element, price);
                    });
                } else {
                    elements.forEach(element => {
                        this.hideCompareLoadingState([element]);
                    });
                }
            });

        } catch (error) {
            console.error('DP: updateCompareDrawerPrices error:', error);
            this.hideCompareLoadingState(priceElements);
        }
    }

    updateComparePriceElement(element, price) {
        const formattedPrice = this.formatMoney(price * 100);
        const sku = element.dataset.productSku;

        const priceRegular = element.querySelector('.f-price__regular');
        const priceItem = priceRegular?.querySelector('.f-price-item--regular, .f-price-item');

        if (priceItem) {
            priceItem.setAttribute('data-dynamic-pricing-active', 'true');
            priceItem.textContent = formattedPrice;
            priceRegular.style.opacity = '1';
            priceRegular.style.display = '';

            const priceSale = element.querySelector('.f-price__sale');
            if (priceSale) {
                priceSale.style.display = 'none';
                priceSale.style.opacity = '0';
            }

            element.classList.remove('f-price--on-sale', 'dp-loading');
            element.classList.add('dp-active', 'dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();
        }

        // Update variant button prices in compare drawer
        const compareItem = element.closest('.compare-item, .comparison-item, [data-compare-item]');
        if (compareItem && sku) {
            this.updateVariantButtonPrices(compareItem, sku, price);
        }

        this.processedElements.add(element);
    }

    hideCompareLoadingState(elements) {
        elements.forEach(element => {
            element.classList.remove('dp-loading');
            element.classList.add('dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');

            if (priceRegular) priceRegular.style.opacity = '1';
            if (priceSale && !element.classList.contains('dp-active')) {
                priceSale.style.opacity = '1';
            }

            this.processedElements.add(element);
        });
    }

    destroy() {
        if (this.priceObserver) {
            this.priceObserver.disconnect();
        }

        if (this.domObserver) {
            this.domObserver.disconnect();
        }

        this.productCardObservers.forEach(obs => obs.disconnect());
        this.productCardObservers.clear();
    }
}


/**
 * DynamicPricing - For Product Detail Pages (PDP)
 */
class DynamicPricing extends DynamicPricingCore {
    constructor(config) {
        super(config);

        if (!config.enabled) {
            return;
        }

        this.mainProductPriceElements = [];
        this.variantPriceMap = new Map();
        this.variantToSku = new Map();
        this.allSkus = [];
        this.isUpdatingPrice = false;
        this.initialFetchComplete = false;
        this.priceObservers = new Map();
        this.processedElements = new WeakSet();
        this.processingElements = new WeakSet(); // Track elements currently being processed
        this.domObserver = null;
        this.dynamicContentTimeout = null;
        this.sectionLoadDebounceTimer = null;
        this.pendingFetches = new Map(); // Track in-flight SKU fetches

        this.init();
    }

    init() {
        if (this.config.skip_on_cart && window.location.pathname.includes('/cart')) {
            return;
        }

        this.identifyMainProductPriceElements();
        this.collectAllSkus();

        if (this.allSkus.length === 0) {
            this.showFallbackPrices();
            return;
        }

        this.showLoadingState();
        this.fetchAllPrices();
        this.setupVariantInterceptors();
        this.setupEventListeners();
        this.interceptFetch();
        this.setupDynamicContentObserver();
    }

    /**
     * Set up MutationObserver for dynamically loaded sections
     */
    setupDynamicContentObserver() {
        this.domObserver = new MutationObserver((mutations) => {
            let hasNewPriceElements = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const priceElements = node.querySelectorAll ?
                                node.querySelectorAll('[data-dynamic-price]') : [];

                            if (node.matches && node.matches('[data-dynamic-price]')) {
                                hasNewPriceElements = true;
                            }

                            if (priceElements.length > 0) {
                                hasNewPriceElements = true;
                            }
                        }
                    }
                }
            }

            if (hasNewPriceElements) {
                this.handleDynamicContent();
            }
        });

        this.domObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        document.addEventListener('shopify:section:load', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('recently-viewed:loaded', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('recommendations:loaded', () => {
            this.handleDynamicContent();
        });

        document.addEventListener('product-recommendations:loaded', () => {
            this.handleDynamicContent();
        });
    }

    /**
     * Handle dynamically loaded content - debounced
     */
    handleDynamicContent() {
        clearTimeout(this.dynamicContentTimeout);
        this.dynamicContentTimeout = setTimeout(() => {
            this._handleDynamicContentInternal();
        }, 100);
    }

    /**
     * Internal implementation of handleDynamicContent
     */
    async _handleDynamicContentInternal() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');
        const newElements = [];
        const newSkus = [];

        allPriceElements.forEach(element => {
            // Skip if already processed AND ready, or currently being processed
            if (this.processedElements.has(element) && element.classList.contains('dp-ready')) {
                return;
            }
            if (this.processingElements.has(element)) {
                return; // Already being handled by another call
            }

            this.processingElements.add(element); // Mark as in-progress immediately
            newElements.push(element);

            const sku = element.dataset.productSku;
            // Check cache AND pending fetches
            if (sku && !newSkus.includes(sku) && !this.priceCache[sku] && !this.pendingFetches.has(sku)) {
                newSkus.push(sku);
            }

            element.classList.add('dp-loading');
            element.classList.remove('dp-ready', 'dp-active');
            element.style.opacity = '1';
            element.style.visibility = 'visible';

            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '0';
            if (priceSale) priceSale.style.opacity = '0';

            if (!element.querySelector('.loading-spinner')) {
                element.insertAdjacentHTML('beforeend', this.createSpinner());
            }
        });

        // Also collect SKUs from new variant buttons
        const newVariantButtons = document.querySelectorAll('button[data-sku]:not([data-dp-processed]), .custom-variant-option-product-card[data-sku]:not([data-dp-processed])');
        newVariantButtons.forEach(button => {
            const sku = button.dataset.sku;
            if (sku && !newSkus.includes(sku) && !this.priceCache[sku] && !this.pendingFetches.has(sku)) {
                newSkus.push(sku);
            }
        });

        if (newElements.length === 0 && newVariantButtons.length === 0) {
            return;
        }

        // Collect SKUs from product JSONs
        const newProductJsons = document.querySelectorAll('[data-product-json]');
        newProductJsons.forEach(element => {
            try {
                const product = JSON.parse(element.textContent);
                const variants = product.variants || [];
                variants.forEach(v => {
                    if (v.sku && !newSkus.includes(v.sku) && !this.priceCache[v.sku] && !this.pendingFetches.has(v.sku)) {
                        newSkus.push(v.sku);
                    }
                });
            } catch (e) {}
        });

        let allPrices = { ...this.priceCache };

        if (newSkus.length > 0) {
            try {
                const cachedPrices = this.getPricesFromCache(newSkus);
                const uncachedSkus = newSkus.filter(sku => !cachedPrices[sku] && !this.pendingFetches.has(sku));

                if (uncachedSkus.length > 0) {
                    // Mark SKUs as pending BEFORE the fetch
                    const fetchPromise = this.fetchPrices(uncachedSkus);
                    uncachedSkus.forEach(sku => this.pendingFetches.set(sku, fetchPromise));

                    try {
                        const newPrices = await fetchPromise;
                        this.updateCache(newPrices);
                        allPrices = { ...this.priceCache, ...newPrices };
                    } finally {
                        // Clear pending status
                        uncachedSkus.forEach(sku => this.pendingFetches.delete(sku));
                    }
                }

                // Wait for any pending fetches for SKUs we need
                const pendingSkus = newSkus.filter(sku => this.pendingFetches.has(sku));
                if (pendingSkus.length > 0) {
                    await Promise.all([...new Set(pendingSkus.map(sku => this.pendingFetches.get(sku)))]);
                    allPrices = { ...this.priceCache };
                }
            } catch (error) {
                console.error('DP: handleDynamicContent fetch error:', error);
            }
        }

        // Update price elements
        newElements.forEach(element => {
            const sku = element.dataset.productSku;
            const price = allPrices[sku];

            if (price !== undefined) {
                this.updatePriceElement(element, price);
            } else {
                element.classList.remove('dp-loading');
                element.classList.add('dp-ready');

                const spinner = element.querySelector('.loading-spinner');
                if (spinner) spinner.remove();

                const priceRegular = element.querySelector('.f-price__regular');
                const priceSale = element.querySelector('.f-price__sale');
                if (priceRegular) priceRegular.style.opacity = '1';
                if (priceSale) priceSale.style.opacity = '1';

                element.style.opacity = '1';
                element.style.visibility = 'visible';
            }

            this.processedElements.add(element);
            this.processingElements.delete(element); // Clear in-progress flag
        });

        // Update ALL variant buttons with new prices
        this.updateAllVariantButtonPrices(allPrices);

        // Mark buttons as processed
        newVariantButtons.forEach(button => {
            button.dataset.dpProcessed = 'true';
        });

        this.updateAllForms(allPrices);
        this.setupEventListeners();
    }

    /**
     * Update variant button data-price attributes
     */
    updateVariantButtonPrices(container, sku, price) {
        const priceInCents = Math.round(price * 100);

        // Update any variant buttons with data-price attribute
        const buttons = container.querySelectorAll(`button[data-sku="${sku}"][data-price]`);
        buttons.forEach(button => {
            button.dataset.price = priceInCents;
        });

        // Update radio inputs and other variant selectors with data-sku
        const radios = container.querySelectorAll(`input[type="radio"][data-sku="${sku}"]`);
        radios.forEach(radio => {
            radio.dataset.price = priceInCents;
        });

        // Update any other elements with data-sku (selects, etc)
        const otherElements = container.querySelectorAll(`select[data-sku="${sku}"], [data-variant-sku="${sku}"]`);
        otherElements.forEach(element => {
            element.dataset.price = priceInCents;
        });
    }

    /**
     * Update variant selectors by variant ID (more reliable than SKU)
     */
    updateVariantSelectorsByVariantId(container, variantId, price) {
        const priceInCents = Math.round(price * 100);

        // Find all variant selectors with this variant ID
        const selectors = container.querySelectorAll(`
            input[type="radio"][data-id="${variantId}"],
            button[data-variant-id="${variantId}"],
            option[value="${variantId}"]
        `);

        selectors.forEach(selector => {
            selector.dataset.price = priceInCents;

            // Also set formatted price if the theme uses it
            if (selector.dataset.currency) {
                selector.dataset.formattedPrice = this.formatMoney(priceInCents);
            }
        });
    }

    /**
     * Update forms in suggested product cards
     */
    updateSuggestedProductForms(pricesMap) {
        // const cardSelectors = '.product-card, .product-item, .card, .product-recommendations, .related-products, .recently-viewed';
        // const cards = document.querySelectorAll(cardSelectors);

        // cards.forEach(card => {
        //     const form = card.querySelector('form[action*="/cart/add"]');
        //     if (!form) return;

        //     const priceElement = card.querySelector('[data-dynamic-price][data-product-sku]');
        //     if (!priceElement) return;

        //     const sku = priceElement.dataset.productSku;
        //     if (sku && pricesMap[sku]) {
        //         let input = form.querySelector('[name="properties[__Dynamic Pricing__]"]');
        //         if (input) {
        //             input.value = pricesMap[sku].toFixed(2);
        //         } else {
        //             input = document.createElement('input');
        //             input.type = 'hidden';
        //             input.name = 'properties[__Dynamic Pricing__]';
        //             input.value = pricesMap[sku].toFixed(2);
        //             form.appendChild(input);
        //         }
        //     }
        // });
    }

    identifyMainProductPriceElements() {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');

        const cardSelectors = [
            '.product-card',
            '.product-item',
            '.card',
            '.product-recommendations',
            '.related-products',
            '.recently-viewed',
            '.complementary-products',
            '[data-product-card]',
            '.recommendation-product'
        ];

        allPriceElements.forEach(element => {
            const isInCard = cardSelectors.some(selector => element.closest(selector));

            if (!isInCard) {
                this.mainProductPriceElements.push(element);
            }
        });
    }

    collectAllSkus() {
        this.collectMainProductVariants();
        this.collectSuggestedProductSkus();
    }

    collectMainProductVariants() {
        const allProductJsons = document.querySelectorAll('[data-product-json]');
        const cardSelectors = '.product-card, .product-item, .card, .product-recommendations, .related-products';

        allProductJsons.forEach((element, index) => {
            if (element.closest(cardSelectors)) {
                return;
            }

            try {
                const product = JSON.parse(element.textContent);

                const variants = product.variants || [];
                variants.forEach(variant => {
                    if (variant.sku && variant.sku.trim()) {
                        const sku = variant.sku.trim();
                        if (!this.allSkus.includes(sku)) {
                            this.allSkus.push(sku);
                        }
                        if (!this.variantToSku.has(variant.id)) {
                            this.variantToSku.set(variant.id, sku);
                        }
                    }
                });
            } catch (e) {}
        });

        // Fallback to ShopifyAnalytics
        if (this.variantToSku.size === 0 && window.ShopifyAnalytics?.meta?.product?.variants) {
            window.ShopifyAnalytics.meta.product.variants.forEach(variant => {
                if (variant.sku && variant.sku.trim()) {
                    const sku = variant.sku.trim();
                    if (!this.allSkus.includes(sku)) this.allSkus.push(sku);
                    if (!this.variantToSku.has(variant.id)) {
                        this.variantToSku.set(variant.id, sku);
                    }
                }
            });
        }

        // Fallback to window.product
        if (this.variantToSku.size === 0 && window.product?.variants) {
            window.product.variants.forEach(variant => {
                if (variant.sku && variant.sku.trim()) {
                    const sku = variant.sku.trim();
                    if (!this.allSkus.includes(sku)) this.allSkus.push(sku);
                    if (!this.variantToSku.has(variant.id)) {
                        this.variantToSku.set(variant.id, sku);
                    }
                }
            });
        }

        // Fallback to price elements
        if (this.variantToSku.size === 0) {
            this.mainProductPriceElements.forEach(el => {
                const sku = el.dataset.productSku;
                if (sku && sku.trim() && !this.allSkus.includes(sku)) {
                    this.allSkus.push(sku.trim());
                }
            });
        }

        const variantButtons = document.querySelectorAll('button[data-sku], .custom-variant-option-product-card[data-sku], input[type="radio"][data-sku]');
        variantButtons.forEach(element => {
            const sku = element.dataset.sku;
            if (sku && sku.trim() && !this.allSkus.includes(sku.trim())) {
                this.allSkus.push(sku.trim());
            }
        });
    }

    collectSuggestedProductSkus() {
        const cardSelectors = [
            '.product-card [data-dynamic-price][data-product-sku]',
            '.product-item [data-dynamic-price][data-product-sku]',
            '.product-recommendations [data-dynamic-price][data-product-sku]',
            '.related-products [data-dynamic-price][data-product-sku]',
            '.recently-viewed [data-dynamic-price][data-product-sku]',
            '.complementary-products [data-dynamic-price][data-product-sku]'
        ];

        const suggestedElements = document.querySelectorAll(cardSelectors.join(', '));

        suggestedElements.forEach(element => {
            const sku = element.dataset.productSku;
            if (sku && sku.trim() && !this.allSkus.includes(sku.trim())) {
                this.allSkus.push(sku.trim());
            }
        });
    }

    async fetchAllPrices() {
        try {
            const cachedPrices = this.getPricesFromCache(this.allSkus);
            const uncachedSkus = this.allSkus.filter(sku => cachedPrices[sku] === undefined);

            let allPrices = { ...cachedPrices };

            if (uncachedSkus.length > 0) {
                const newPrices = await this.fetchPrices(uncachedSkus);
                this.updateCache(newPrices);
                allPrices = { ...allPrices, ...newPrices };
            }

            this.buildVariantPriceMap(allPrices);
            this.initialFetchComplete = true;
            this.preloadAllVariantButtonPrices(allPrices);
            this.updateMainProductPrice();
            this.updateSuggestedProducts(allPrices);
            this.updateAllVariantButtonPrices(allPrices);
            this.hideLoadingState();
            this.setupPriceProtection();
            this.setupSuggestedProductListeners();

        } catch (error) {
            console.error('DP: fetchAllPrices error:', error);
            this.hideLoadingState();
            this.showFallbackPrices();
        }
    }

    /**
     * Update ALL variant buttons on the page with dynamic prices
     */
    updateAllVariantButtonPrices(pricesMap) {
        const allVariantButtons = document.querySelectorAll('button[data-sku][data-price], .custom-variant-option-product-card[data-sku][data-price]');

        allVariantButtons.forEach(button => {
            const sku = button.dataset.sku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                const priceInCents = Math.round(price * 100);
                button.dataset.price = priceInCents;
            }
        });

        const allRadioInputs = document.querySelectorAll('input[type="radio"][data-sku][data-price]');
        allRadioInputs.forEach(radio => {
            const sku = radio.dataset.sku;
            const price = pricesMap[sku];

            if (price !== undefined) {
                radio.dataset.price = Math.round(price * 100);
            }
        });
    }

    /**
     * Preload all variant button prices for the main product
     */
    preloadAllVariantButtonPrices(pricesMap) {
        // Get the main product container
        const mainProduct = document.querySelector('.product:not(.product-card), .product-single, .product-template, [data-product-form]');
        if (!mainProduct) return;

        this.variantToSku.forEach((sku, variantId) => {
            const price = pricesMap[sku];
            if (price !== undefined) {
                // Update by SKU
                this.updateVariantButtonPrices(mainProduct, sku, price);
                // Update by variant ID (more reliable for radio inputs)
                this.updateVariantSelectorsByVariantId(mainProduct, variantId, price);
            }
        });
    }

    buildVariantPriceMap(pricesMap) {
        this.variantToSku.forEach((sku, variantId) => {
            if (pricesMap[sku] !== undefined) {
                this.variantPriceMap.set(variantId, pricesMap[sku]);
            }
        });
    }

    getPriceForVariant(variantId) {
        const numericId = parseInt(variantId);

        let price = this.variantPriceMap.get(numericId);
        if (price !== undefined) return price;

        let sku = this.variantToSku.get(numericId);
        if (!sku) sku = this.lookupSkuForVariant(numericId);

        if (sku && this.priceCache[sku] !== undefined) {
            price = this.priceCache[sku];
            this.variantPriceMap.set(numericId, price);
            this.variantToSku.set(numericId, sku);
            return price;
        }

        return undefined;
    }

    lookupSkuForVariant(variantId) {
        const productJsons = document.querySelectorAll('[data-product-json]');
        for (const el of productJsons) {
            try {
                const product = JSON.parse(el.textContent);
                const variant = product.variants?.find(v => v.id == variantId);
                if (variant?.sku) return variant.sku;
            } catch (e) {}
        }

        const saVariant = window.ShopifyAnalytics?.meta?.product?.variants?.find(v => v.id == variantId);
        if (saVariant?.sku) return saVariant.sku;

        const wpVariant = window.product?.variants?.find(v => v.id == variantId);
        if (wpVariant?.sku) return wpVariant.sku;

        return null;
    }

    getCurrentVariantId() {
        const forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const form of forms) {
            if (!form.closest('.product-card, .product-item, .card')) {
                const input = form.querySelector('[name="id"]');
                if (input?.value) return parseInt(input.value);
            }
        }
        return null;
    }

    updateMainProductPrice() {
        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        let price = this.getPriceForVariant(variantId);

        if (price === undefined && this.mainProductPriceElements.length > 0) {
            const sku = this.mainProductPriceElements[0].dataset.productSku;
            if (sku) {
                price = this.priceCache[sku];
                if (price !== undefined) {
                    this.variantPriceMap.set(variantId, price);
                    this.variantToSku.set(variantId, sku);
                }
            }
        }

        if (price !== undefined) {
            this.updateMainPriceElements(price, variantId);
            // this.updateHiddenInput(price);
        }
    }

    updateMainPriceElements(price, variantId) {
        this.isUpdatingPrice = true;
        const formattedPrice = this.formatMoney(price * 100);

        this.mainProductPriceElements.forEach(element => {
            element.setAttribute('data-variant-id', variantId);
            element.setAttribute('data-expected-price', price);

            const priceRegular = element.querySelector('.f-price__regular');
            const priceItem = priceRegular?.querySelector('.f-price-item--regular');

            if (priceItem) {
                priceItem.setAttribute('data-dynamic-pricing-active', 'true');
                priceItem.textContent = formattedPrice;
                priceRegular.style.display = '';
                priceRegular.style.opacity = '1';
            }

            const priceSale = element.querySelector('.f-price__sale');
            if (priceSale) priceSale.style.display = 'none';

            element.classList.remove('f-price--on-sale', 'dp-loading');
            element.classList.add('dp-active', 'dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            element.style.opacity = '1';
            element.style.visibility = 'visible';

            this.processedElements.add(element);
        });

        // Update variant button prices on main product
        const sku = this.variantToSku.get(variantId);
        if (sku) {
            // Look for the main product container (not in product cards)
            const mainProduct = document.querySelector('.product:not(.product-card), .product-single, .product-template');
            if (mainProduct) {
                this.updateVariantButtonPrices(mainProduct, sku, price);
                this.updateVariantSelectorsByVariantId(mainProduct, variantId, price);
            } else {
                // Fallback update all selectors on page that aren't in product cards
                const buttons = document.querySelectorAll(`button[data-sku="${sku}"][data-price]`);
                buttons.forEach(button => {
                    if (!button.closest('.product-card, .product-item, .card')) {
                        button.dataset.price = Math.round(price * 100);
                    }
                });

                // Update radio inputs
                const radios = document.querySelectorAll(`input[type="radio"][data-id="${variantId}"]`);
                radios.forEach(radio => {
                    if (!radio.closest('.product-card, .product-item, .card')) {
                        radio.dataset.price = Math.round(price * 100);
                        if (radio.dataset.currency) {
                            radio.dataset.formattedPrice = formattedPrice;
                        }
                    }
                });
            }
        }

        setTimeout(() => { this.isUpdatingPrice = false; }, 50);
    }

    updateSuggestedProducts(pricesMap) {
        const allPriceElements = document.querySelectorAll('[data-dynamic-price]');

        allPriceElements.forEach(element => {
            if (this.mainProductPriceElements.includes(element)) return;

            const sku = element.dataset.productSku;
            if (!sku) return;

            const price = pricesMap[sku];
            if (price !== undefined) {
                const formattedPrice = this.formatMoney(price * 100);

                const priceRegular = element.querySelector('.f-price__regular');
                const priceItem = priceRegular?.querySelector('.f-price-item--regular');

                if (priceItem) {
                    priceItem.setAttribute('data-dynamic-pricing-active', 'true');
                    priceItem.textContent = formattedPrice;
                    priceRegular.style.display = '';
                    priceRegular.style.opacity = '1';
                }

                const priceSale = element.querySelector('.f-price__sale');
                if (priceSale) priceSale.style.display = 'none';

                element.classList.remove('f-price--on-sale', 'dp-loading');
                element.classList.add('dp-active', 'dp-ready');

                // Update variant button prices in suggested products
                const productCard = element.closest('.product-card, .product-item, .product, [data-product-card], .card');
                if (productCard) {
                    this.updateVariantButtonPrices(productCard, sku, price);
                }
            }

            element.classList.remove('dp-loading');
            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();
            element.style.opacity = '1';
            element.style.visibility = 'visible';

            this.processedElements.add(element);
        });

        // this.updateSuggestedProductForms(pricesMap);
    }

    updateHiddenInput(price) {
        // const forms = document.querySelectorAll('form[action*="/cart/add"]');
        // for (const form of forms) {
        //     if (!form.closest('.product-card, .product-item, .card')) {
        //         let input = form.querySelector('[name="properties[__Dynamic Pricing__]"]');
        //         if (input) {
        //             input.value = price.toFixed(2);
        //         } else {
        //             input = document.createElement('input');
        //             input.type = 'hidden';
        //             input.name = 'properties[__Dynamic Pricing__]';
        //             input.value = price.toFixed(2);
        //             form.appendChild(input);
        //         }
        //         break;
        //     }
        // }
    }

    setupEventListeners() {
        document.addEventListener('variant:change', (e) => {
            this.handleVariantChange(e.detail?.variant?.id);
        });

        document.addEventListener('product:variant:change', (e) => {
            this.handleVariantChange(e.detail?.variant?.id);
        });

        const forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const form of forms) {
            if (!form.closest('.product-card, .product-item, .card')) {
                const variantInput = form.querySelector('[name="id"]');
                if (variantInput) {
                    variantInput.addEventListener('change', () => {
                        this.handleVariantChange(variantInput.value);
                    });

                    const observer = new MutationObserver(() => {
                        this.handleVariantChange(variantInput.value);
                    });
                    observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

                    let lastValue = variantInput.value;
                    setInterval(() => {
                        if (variantInput.value !== lastValue) {
                            lastValue = variantInput.value;
                            this.handleVariantChange(variantInput.value);
                        }
                    }, 100);
                }

                form.querySelectorAll('input[name^="option"], select[name^="option"]').forEach(selector => {
                    selector.addEventListener('change', () => {
                        setTimeout(() => this.updateMainProductPrice(), 50);
                    });
                });

                break;
            }
        }
    }

    handleVariantChange(variantId) {
        if (!variantId || !this.initialFetchComplete) return;

        const price = this.getPriceForVariant(parseInt(variantId));
        if (price !== undefined) {
            this.updateMainPriceElements(price, parseInt(variantId));
            // this.updateHiddenInput(price);
        }
    }

    interceptFetch() {
        const originalFetch = window.fetch;
        const self = this;

        window.fetch = async function(...args) {
            const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

            // BEFORE the fetch completes, hide prices if it's a section render
            if (url && url.includes('section_id=')) {
                self.mainProductPriceElements.forEach(el => {
                    el.classList.add('dp-updating');
                    el.classList.remove('dp-ready');
                });
            }

            const response = await originalFetch.apply(this, args);

            if (url && url.includes('section_id=')) {
                // Single debounced call instead of 3 separate timeouts
                clearTimeout(self.sectionLoadDebounceTimer);
                self.sectionLoadDebounceTimer = setTimeout(() => {
                    self.identifyMainProductPriceElements();
                    self.updateMainProductPrice();
                    self.handleDynamicContent(); // This is already debounced
                }, 100);
            }

            return response;
        };
    }


    setupPriceProtection() {
        this.mainProductPriceElements.forEach(element => {
            const priceItem = element.querySelector('.f-price-item--regular');
            if (!priceItem) return;

            const observer = new MutationObserver(() => {
                if (this.isUpdatingPrice) return;

                const variantId = parseInt(element.dataset.variantId);
                const expectedPrice = this.getPriceForVariant(variantId);

                if (expectedPrice !== undefined) {
                    const expectedFormatted = this.formatMoney(expectedPrice * 100);
                    if (priceItem.textContent.trim() !== expectedFormatted) {
                        this.isUpdatingPrice = true;
                        priceItem.textContent = expectedFormatted;
                        setTimeout(() => { this.isUpdatingPrice = false; }, 50);
                    }
                }
            });

            observer.observe(priceItem, { characterData: true, subtree: true, childList: true });
            this.priceObservers.set(element, observer);
        });
    }

    showLoadingState() {
        document.querySelectorAll('[data-dynamic-price]').forEach(element => {
            element.classList.add('dp-loading');
            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.display = 'none';
            if (priceSale) priceSale.style.display = 'none';
            if (!element.querySelector('.loading-spinner')) {
                element.insertAdjacentHTML('beforeend', this.createSpinner());
            }
        });
    }

    hideLoadingState() {
        document.querySelectorAll('[data-dynamic-price]').forEach(element => {
            element.classList.remove('dp-loading');
            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();
            const priceRegular = element.querySelector('.f-price__regular');
            if (priceRegular) priceRegular.style.display = '';

            this.processedElements.add(element);
        });
    }

    showFallbackPrices() {
        document.querySelectorAll('[data-dynamic-price]').forEach(element => {
            element.classList.add('dp-ready');
            element.classList.remove('dp-loading');
            element.style.opacity = '';
            element.style.visibility = '';
            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();
            const priceRegular = element.querySelector('.f-price__regular');
            const priceSale = element.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.display = '';
            if (priceSale) priceSale.style.display = '';

            this.processedElements.add(element);
        });
    }

    /**
     * Intercept variant changes BEFORE theme updates price (capture phase)
     */
    setupVariantInterceptors() {
        try {
            const mainProduct = document.querySelector('.product:not(.product-card), .product-single, .product-template, [data-product-form]');

            if (!mainProduct) {
                return;
            }

            const radioButtons = mainProduct.querySelectorAll('input[type="radio"][data-id]');

            const self = this;

            radioButtons.forEach(radio => {
                radio.addEventListener('change', function(e) {
                    self.showUpdatingState();
                }, true);
            });

        } catch (error) {
            console.error('DP: setupVariantInterceptors error:', error);
        }
    }

    /**
     * Show updating state - hide prices immediately
     */
    showUpdatingState() {
        try {
            this.mainProductPriceElements.forEach(el => {
                el.classList.add('dp-updating');
                el.classList.remove('dp-ready');
            });
        } catch (error) {
            console.error('DP: showUpdatingState error:', error);
        }
    }
    /**
     * Update a single price element with dynamic price
     */
    updatePriceElement(element, price) {
        const formattedPrice = this.formatMoney(price * 100);
        const sku = element.dataset.productSku;

        const priceRegular = element.querySelector('.f-price__regular');
        const priceItem = priceRegular?.querySelector('.f-price-item--regular');

        if (priceItem) {
            priceItem.setAttribute('data-dynamic-pricing-active', 'true');
            priceItem.textContent = formattedPrice;
            priceRegular.style.display = '';
            priceRegular.style.opacity = '1';

            const priceSale = element.querySelector('.f-price__sale');
            if (priceSale) {
                priceSale.style.display = 'none';
            }

            element.classList.remove('f-price--on-sale', 'dp-loading', 'dp-updating');
            element.classList.add('dp-active', 'dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            element.style.opacity = '1';
            element.style.visibility = 'visible';
        }

        // Update variant button prices in the same product card
        const productCard = element.closest('.product-card, .product-item, .product, [data-product-card], .card');
        if (productCard && sku) {
            this.updateVariantButtonPrices(productCard, sku, price);
        }

        this.processedElements.add(element);
    }

    /**
     * Update all forms with dynamic pricing hidden input
     */
    updateAllForms(pricesMap) {
        // const productForms = document.querySelectorAll('form[action*="/cart/add"]');

        // productForms.forEach(form => {
        //     const variantInput = form.querySelector('[name="id"]');
        //     if (!variantInput || !variantInput.value) {
        //         return;
        //     }

        //     const variantId = variantInput.value;
        //     const sku = this.lookupSkuForVariant(variantId);

        //     if (sku && pricesMap[sku]) {
        //         let input = form.querySelector('[name="properties[__Dynamic Pricing__]"]');
        //         if (input) {
        //             input.value = pricesMap[sku].toFixed(2);
        //         } else {
        //             input = document.createElement('input');
        //             input.type = 'hidden';
        //             input.name = 'properties[__Dynamic Pricing__]';
        //             input.value = pricesMap[sku].toFixed(2);
        //             form.appendChild(input);
        //         }
        //     }
        // });
    }

    /**
     * Set up event listeners for suggested product cards
     */
    setupSuggestedProductListeners() {
        const cardSelectors = [
            '.product-card',
            '.product-item',
            '.card',
            '.product-recommendations',
            '.related-products',
            '.recently-viewed',
            '.complementary-products'
        ];

        const self = this;

        // Find all variant pickers in product cards
        let variantPickers = [];

        cardSelectors.forEach(cardSelector => {
            const pickers = document.querySelectorAll(`
            ${cardSelector} input[type="radio"][data-id],
            ${cardSelector} select[name^="option"],
            ${cardSelector} .swatch-element,
            ${cardSelector} .custom-variant-option-product-card
        `);
            variantPickers = [...variantPickers, ...Array.from(pickers)];
        });

        // Remove duplicates
        variantPickers = [...new Set(variantPickers)];

        variantPickers.forEach(picker => {
            // Skip if already has listener
            if (picker.dataset.dpListener) return;
            picker.dataset.dpListener = 'true';

            const eventType = (picker.tagName === 'SELECT' || picker.type === 'radio') ? 'change' : 'click';

            // Capture phase - hide price before theme updates
            picker.addEventListener(eventType, function(e) {
                const productCard = this.closest('.product-card, .product-item, .card, [data-product-card]');
                if (!productCard) return;

                const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
                priceElements.forEach(el => {
                    el.classList.add('dp-updating');
                    el.classList.remove('dp-ready', 'dp-active');
                });
            }, true);

            picker.addEventListener(eventType, function(e) {
                const productCard = this.closest('.product-card, .product-item, .card, [data-product-card]');
                if (!productCard) return;

                const form = productCard.querySelector('form[action*="/cart/add"]');
                if (form) {
                    setTimeout(() => {
                        self.handleProductCardVariantChange(form, productCard);
                    }, 50);
                }
            }, false);
        });
    }

    /**
     * Handle variant change on a product card
     */
    handleProductCardVariantChange(form, productCard) {
        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput || !variantInput.value) {
            this.showProductCardPrices(productCard);
            return;
        }

        const variantId = parseInt(variantInput.value);
        const sku = this.lookupSkuForVariant(variantId);

        if (sku && this.priceCache[sku]) {
            this.updateProductCardPrice(productCard, variantId, sku, this.priceCache[sku]);
        } else if (sku) {
            this.fetchAndUpdateProductCardPrice(sku, variantId, productCard);
        } else {
            this.showProductCardPrices(productCard);
        }
    }

    /**
     * Update price on a product card
     */
    updateProductCardPrice(productCard, variantId, sku, price) {
        const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
        const formattedPrice = this.formatMoney(price * 100);

        priceElements.forEach(element => {
            element.setAttribute('data-variant-id', variantId);

            const priceRegular = element.querySelector('.f-price__regular');
            const priceItem = priceRegular?.querySelector('.f-price-item--regular');

            if (priceItem) {
                priceItem.setAttribute('data-dynamic-pricing-active', 'true');
                priceItem.textContent = formattedPrice;
                priceRegular.style.opacity = '1';
                priceRegular.style.display = '';
            }

            const priceSale = element.querySelector('.f-price__sale');
            if (priceSale) {
                priceSale.style.display = 'none';
                priceSale.style.opacity = '0';
            }

            element.classList.remove('f-price--on-sale', 'dp-loading', 'dp-updating');
            element.classList.add('dp-active', 'dp-ready');

            const spinner = element.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            this.processedElements.add(element);
        });

        // Update variant button prices
        if (sku) {
            this.updateVariantButtonPrices(productCard, sku, price);
            this.updateVariantSelectorsByVariantId(productCard, variantId, price);
        }

        // Update form hidden input
        // const form = productCard.querySelector('form[action*="/cart/add"]');
        // if (form) {
        //     let input = form.querySelector('[name="properties[__Dynamic Pricing__]"]');
        //     if (input) {
        //         input.value = price.toFixed(2);
        //     } else {
        //         input = document.createElement('input');
        //         input.type = 'hidden';
        //         input.name = 'properties[__Dynamic Pricing__]';
        //         input.value = price.toFixed(2);
        //         form.appendChild(input);
        //     }
        // }
    }

    /**
     * Fetch price and update product card
     */
    async fetchAndUpdateProductCardPrice(sku, variantId, productCard) {
        // Check if already fetching this SKU
        if (this.pendingFetches.has(sku)) {
            try {
                await this.pendingFetches.get(sku);
                if (this.priceCache[sku]) {
                    this.updateProductCardPrice(productCard, variantId, sku, this.priceCache[sku]);
                } else {
                    this.showProductCardPrices(productCard);
                }
            } catch (error) {
                this.showProductCardPrices(productCard);
            }
            return;
        }

        try {
            const fetchPromise = this.fetchPrices([sku]);
            this.pendingFetches.set(sku, fetchPromise);

            const prices = await fetchPromise;
            this.updateCache(prices);
            this.pendingFetches.delete(sku);

            if (prices[sku]) {
                this.updateProductCardPrice(productCard, variantId, sku, prices[sku]);
            } else {
                this.showProductCardPrices(productCard);
            }
        } catch (error) {
            console.error('DP: fetchAndUpdateProductCardPrice error:', error);
            this.pendingFetches.delete(sku);
            this.showProductCardPrices(productCard);
        }
    }

    /**
     * Show original prices on product card (fallback)
     */
    showProductCardPrices(productCard) {
        const priceElements = productCard.querySelectorAll('[data-dynamic-price]');
        priceElements.forEach(el => {
            el.classList.remove('dp-updating', 'dp-loading');
            el.classList.add('dp-ready');

            const spinner = el.querySelector('.loading-spinner');
            if (spinner) spinner.remove();

            const priceRegular = el.querySelector('.f-price__regular');
            const priceSale = el.querySelector('.f-price__sale');
            if (priceRegular) priceRegular.style.opacity = '1';
            if (priceSale && !el.classList.contains('dp-active')) {
                priceSale.style.opacity = '1';
            }

            this.processedElements.add(el);
        });
    }

    destroy() {
        this.priceObservers.forEach(observer => observer.disconnect());
        this.priceObservers.clear();

        if (this.domObserver) {
            this.domObserver.disconnect();
        }
    }
}

function initializeDynamicPricing() {
    const config = window.dynamicPricingConfig;

    if (!config) {
        setTimeout(initializeDynamicPricing, 50);
        return;
    }

    const hasShipTo = config.shipto_id && config.shipto_id !== 0;

    if (hasShipTo && config.enabled) {
        // Activate the CSS hiding — only now do prices get hidden
        document.body.classList.add('dp-enabled');

        const isCollectionPage = document.body.classList.contains('collection-template') ||
            document.body.classList.contains('search-template') ||
            window.location.pathname.includes('/collections/') ||
            window.location.pathname.includes('/search');

        const isProductPage = document.body.classList.contains('product-template') ||
            window.location.pathname.includes('/products/');

        const isCartPage = window.location.pathname.includes('/cart');

        if ((isCollectionPage || isCartPage) && !isProductPage) {
            window.dpInstance = new DynamicPricingBrowse(config);
        } else {
            window.dpInstance = new DynamicPricing(config);
        }
    }

    new DynamicPricingCart(config);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDynamicPricing);
} else {
    initializeDynamicPricing();
}

window.DynamicPricingBrowse = DynamicPricingBrowse;
window.DynamicPricing = DynamicPricing;